import * as amplitude from '@amplitude/analytics-browser';

import { Logger } from '../logger';
import { ONETRUST_CATEGORIES, OneTrustService } from '../oneTrust';

const AUTOCAPTURE_CONFIG = {
  attribution: true,
  elementInteractions: true,
  fileDownloads: true,
  formInteractions: true,
  frustrationInteractions: true,
  pageViews: { trackHistoryChanges: 'all' },
  sessions: true,
  webVitals: true,
  networkTracking: false,
};

const HTTP_URL_RE = /^https?:\/\//i;

let instance = undefined;

export function initAmplitude(apiKey, options = {}) {
  if (typeof window === 'undefined' || !apiKey || instance) {
    return;
  }

  function initialize() {
    instance = amplitude.createInstance();
    instance
      .init(apiKey, {
        autocapture: AUTOCAPTURE_CONFIG,
        fetchRemoteConfig: false,
        defaultTracking: false,
        ...options,
      })
      .promise.then(() => {
        instance.add(makeStripQueryPlugin());
        updateConsentState();
      })
      .catch((error) => {
        Logger.error('initAmplitude failed', error);
      });

    window.addEventListener('OneTrustGroupsUpdated', () => {
      updateConsentState();
    });
  }

  if (window.OnetrustActiveGroups) {
    initialize();
  } else {
    window.addEventListener(
      'OneTrustGroupsUpdated',
      () => {
        initialize();
      },
      { once: true }
    );
  }
}

function updateConsentState() {
  if (!instance) {
    return;
  }

  OneTrustService.checkConsent(ONETRUST_CATEGORIES.performance)
    .then((hasConsent) => {
      instance.setOptOut(!hasConsent);
    })
    .catch((error) => {
      Logger.error('Failed to check consent for Amplitude', error);
    });
}

export function resetAmplitude() {
  instance?.reset();
}

function stripNonUtmParams(urlStr) {
  try {
    const url = new URL(urlStr);
    const utmParams = new URLSearchParams();

    const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    UTM_KEYS.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value) {
        utmParams.set(key, value);
      }
    });

    url.search = utmParams.toString();
    return url.toString();
  } catch {
    return urlStr;
  }
}

function makeStripQueryPlugin() {
  return {
    name: 'strip-non-utm-query-params',
    execute: async (event) => {
      const props = event.event_properties;
      if (!props) {
        return event;
      }

      for (const [key, value] of Object.entries(props)) {
        if (typeof value === 'string' && HTTP_URL_RE.test(value)) {
          props[key] = stripNonUtmParams(value);
        }
      }

      return event;
    },
  };
}
